import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { DocumentTextIcon, ChevronDownIcon, ScaleIcon, ShieldCheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const LegalDocument = ({ content, icon: IconComponent }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Choose icon based on document type or use provided icon
  const Icon = IconComponent || DocumentTextIcon;

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Navigation Bar */}
      <motion.nav 
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b border-gray-800/30 bg-gray-900/80"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="container mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <motion.a 
              href="/"
              className="cursor-pointer flex items-center"
              whileHover={{ scale: 1.05 }}
            >
              <img 
                src="/logo.png" 
                alt="Octagon Infinity" 
                className="h-16 sm:h-20 w-auto"
              />
            </motion.a>
            
            <motion.a
              href="/"
              className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white transition-colors duration-300 text-sm sm:text-base"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Back to Home
            </motion.a>
          </div>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <motion.section 
        className="pt-32 pb-16 px-4 sm:px-6 relative overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        {/* Background Effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-96 h-96 bg-amber-600/10 rounded-full blur-3xl -top-48 -left-48"></div>
          <div className="absolute w-96 h-96 bg-blue-600/10 rounded-full blur-3xl -bottom-48 -right-48"></div>
        </div>

        <div className="container mx-auto max-w-4xl relative z-10">
          <motion.div 
            className="text-center mb-12"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <Icon className="w-16 h-16 mx-auto mb-6 text-amber-500" />
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 bg-gradient-to-r from-amber-500 via-blue-400 to-cyan-400 bg-clip-text text-transparent">
              {content.title}
            </h1>
            <p className="text-xl sm:text-2xl text-gray-300 mb-2">
              {content.subtitle}
            </p>
            <p className="text-sm text-gray-500">
              {content.effectiveDate}
            </p>
            <p className="text-sm text-gray-500">
              {content.lastUpdated}
            </p>
          </motion.div>

          {/* Table of Contents */}
          <motion.div 
            className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-gray-700/50 mb-12"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <h2 className="text-2xl font-bold mb-6 text-amber-500">Table of Contents</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {content.sections.map((section, index) => (
                <motion.button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className="text-left px-4 py-3 rounded-lg bg-gray-700/30 hover:bg-gray-700/50 transition-all duration-300 border border-gray-600/30 hover:border-amber-500/50"
                  whileHover={{ x: 5 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="text-gray-300 text-sm">{section.title}</span>
                </motion.button>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Content Sections */}
      <section className="pb-20 px-4 sm:px-6">
        <div className="container mx-auto max-w-4xl">
          {content.sections.map((section, index) => (
            <motion.div
              key={section.id}
              id={section.id}
              className="mb-16 scroll-mt-32"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: index * 0.05 }}
            >
              <div className="bg-gray-800/30 backdrop-blur-sm rounded-2xl p-6 sm:p-8 lg:p-10 border border-gray-700/50 hover:border-gray-600/50 transition-all duration-300">
                <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-amber-500">
                  {section.title}
                </h2>
                <div className="text-gray-300 leading-relaxed whitespace-pre-line">
                  {section.content}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 border-t border-gray-800 py-8 px-4 sm:px-6">
        <div className="container mx-auto max-w-4xl text-center">
          <p className="text-gray-400 text-sm mb-4">
            © 2025 Octagon Infinity. All rights reserved.
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <a href="/whitepaper" className="text-gray-400 hover:text-white transition-colors">
              Whitepaper
            </a>
            <a href="/privacy-policy" className="text-gray-400 hover:text-white transition-colors">
              Privacy Policy
            </a>
            <a href="/terms-of-service" className="text-gray-400 hover:text-white transition-colors">
              Terms of Service
            </a>
            <a href="/risk-disclosure" className="text-gray-400 hover:text-white transition-colors">
              Risk Disclosure
            </a>
          </div>
        </div>
      </footer>

      {/* Scroll to Top Button */}
      <motion.button
        className="fixed bottom-8 right-8 bg-amber-600 hover:bg-amber-700 text-white p-4 rounded-full shadow-lg hover:shadow-amber-500/25 transition-all duration-300"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
      >
        <ChevronDownIcon className="w-6 h-6 rotate-180" />
      </motion.button>
    </div>
  );
};

export default LegalDocument;
